// Safe for the client to import: metadata only, no question engine or answers.
export const COURSES=[
  {id:'algebra',name:'Algebra 1'},
  {id:'prealgebra',name:'Prealgebra'},
  {id:'grade8',name:'Grade 8 Math'},
  {id:'grade1',name:'Puzzle Adventures · Grade 1'},
  {id:'k5',name:'Puzzle Adventures · K-5 Kindergarten'},
  {id:'k4',name:'Puzzle Adventures · K-4 Preschool'},
  {id:'grade2',name:'Puzzle Adventures · Grade 2'},
  {id:'grade3',name:'Grade 3 Math'},
  {id:'early',name:'Number adventures · current foundations'},
  {id:'grade4',name:'Grade 4 Math'},
  {id:'grade5',name:'Grade 5 Math'},
  {id:'algebra2',name:'Algebra II'},
  {id:'calculus1',name:'Calculus I'},
  {id:'calculus2',name:'Calculus II'},
  {id:'grade6',name:'Grade 6 Math'},
  {id:'grade7',name:'Grade 7 Math'},
  {id:'geometry',name:'Geometry'},
  {id:'precalculus',name:'Precalculus'},
  {id:'integrated1',name:'Integrated Math I'},
  {id:'integrated2',name:'Integrated Math II'},
  {id:'linalg',name:'Linear Algebra'},
  {id:'integrated3',name:'Integrated Math III'},
  {id:'integrated1-honors',name:'Integrated Math I Honors'},
  {id:'integrated2-honors',name:'Integrated Math II Honors'},
  {id:'integrated3-honors',name:'Integrated Math III Honors'},
  {id:'sat-fundamentals',name:'SAT Math Foundations'},
  {id:'sat-prep',name:'SAT Math Mastery'},
  {id:'probability-statistics',name:'Probability & Statistics'},
  {id:'discrete-mathematics',name:'Discrete Mathematics'},
] as const;
export const COURSE_IDS=['algebra','prealgebra','grade8','grade3','grade2','grade1','k5','k4','early','grade4','grade5','algebra2','calculus1','calculus2','grade6','grade7','geometry','precalculus','integrated1','integrated2','linalg','integrated3','integrated1-honors','integrated2-honors','integrated3-honors','sat-fundamentals','sat-prep','probability-statistics','discrete-mathematics'] as const;
export type CourseId=typeof COURSE_IDS[number];
export const courseName=(id:CourseId)=>COURSES.find(c=>c.id===id)!.name;

export const isAdventure=(id:string|undefined)=>['grade2','grade1','k5','k4'].includes(id??'');
export const isYounger=(id:string|undefined)=>isAdventure(id)||id==='early'||id==='grade3';

// Display sections, in picker order, each listing its courses in order. This one list is both the course
// picker's order and the section membership, so the client keeps no second copy. (COURSES above keeps
// its registration order, which other code relies on.) AP and Bridge to College have no courses yet.
export const COURSE_SECTIONS=[
 {id:'early-learners',name:'Early Learners',courses:['k4','k5','grade1','grade2','grade3','early']},
 {id:'elementary',name:'Elementary',courses:['grade4','grade5']},
 {id:'middle-school',name:'Middle School',courses:['grade6','grade7','prealgebra','grade8']},
 {id:'traditional-high-school',name:'High School Traditional',courses:['algebra','geometry','algebra2','precalculus']},
 {id:'integrated-math',name:'Integrated Math',courses:['integrated1','integrated1-honors','integrated2','integrated2-honors','integrated3','integrated3-honors']},
 {id:'test-prep',name:'Test Prep',courses:['sat-fundamentals','sat-prep']},
 {id:'ap',name:'AP',courses:[]},
 {id:'bridge-college',name:'Bridge to College Math I–III',courses:[]},
 {id:'university',name:'University',courses:['calculus1','calculus2','linalg','probability-statistics','discrete-mathematics']},
] as const satisfies readonly {id:string;name:string;courses:readonly CourseId[]}[];
export type CourseSectionId=typeof COURSE_SECTIONS[number]['id'];
export const COURSE_ORDER:readonly CourseId[]=COURSE_SECTIONS.flatMap(s=>s.courses as readonly CourseId[]);
export const COURSE_SECTION_MEMBERSHIP:Partial<Record<CourseId,CourseSectionId>>=Object.fromEntries(COURSE_SECTIONS.flatMap(s=>(s.courses as readonly CourseId[]).map(c=>[c,s.id])));

// Learning tracks: the one source of truth for progression (docs/plans/ONBOARDING.md). Every track shares
// the grade-school courses (Pre-K through Grade 8, one per school grade) and the upper courses from
// Precalculus on. Tracks differ only in the three high-school years, which line up year by year
// (Algebra 1 ↔ Integrated I ↔ Integrated I Honors, Geometry ↔ II, Algebra II ↔ III). So every track is the
// same length, and position i is the same school year on each. The starting course, placement moves and
// promotion all move along the child's track.
export const TRACK_IDS=['traditional','integrated','integrated-honors'] as const;
export type Track=typeof TRACK_IDS[number];
export const DEFAULT_TRACK:Track='traditional';
const GRADE_SCHOOL=['k4','k5','grade1','grade2','grade3','grade4','grade5','grade6','grade7','grade8'] as const satisfies readonly CourseId[];
const UPPER=['precalculus','calculus1','calculus2','linalg'] as const satisfies readonly CourseId[];
export const TRACKS={
  traditional:[...GRADE_SCHOOL,'algebra','geometry','algebra2',...UPPER],
  integrated:[...GRADE_SCHOOL,'integrated1','integrated2','integrated3',...UPPER],
  'integrated-honors':[...GRADE_SCHOOL,'integrated1-honors','integrated2-honors','integrated3-honors',...UPPER],
} as const satisfies Record<Track,readonly CourseId[]>;
// Parent-facing words for each track.
export const TRACK_LABELS:Record<Track,{name:string;detail:string}>={
  traditional:{name:'Traditional',detail:'Algebra 1, Geometry, Algebra 2'},
  integrated:{name:'Integrated',detail:'Math I, II, III'},
  'integrated-honors':{name:'Integrated Honors',detail:'Math I, II, III Honors'},
};
// Courses past the end of a track. They are offered to the parent as a choice, never moved into
// automatically.
export const ELECTIVES=['probability-statistics','discrete-mathematics'] as const satisfies readonly CourseId[];
// Prealgebra is a bridge off the default path into high-school math, so finishing it moves on exactly as
// finishing Grade 8 does. SAT courses are test prep, not progression: Foundations leads to Mastery, and
// finishing Mastery offers the way back to the track (tracks.ts).
const BRIDGE_TO:Partial<Record<CourseId,CourseId>>={prealgebra:'grade8'};
const SIDE_NEXT:Partial<Record<CourseId,CourseId>>={'sat-fundamentals':'sat-prep'};

export const isTrack=(value:unknown):value is Track=>TRACK_IDS.includes(value as Track);
const trackList=(track:Track)=>TRACKS[track] as readonly CourseId[];
// The school year a course stands for (its index on any track), or -1 for a course on no track.
export const trackPosition=(course:CourseId):number=>{for(const t of TRACK_IDS){const i=trackList(t).indexOf(course);if(i>=0)return i;}return -1;};
export const onTrack=(course:CourseId,track:Track)=>trackList(track).includes(course);
export const onAnyTrack=(course:CourseId)=>trackPosition(course)>=0;
// The track a course implies by itself: integrated and Honors courses name theirs; every other course
// (the shared grades, the upper courses, side courses) reads as Traditional.
export const trackOfCourse=(course:CourseId):Track=>TRACK_IDS.find(t=>t!=='traditional'&&onTrack(course,t)&&!onTrack(course,'traditional'))??DEFAULT_TRACK;

// The Traditional track, kept for existing callers.
export const COURSE_LADDER=TRACKS.traditional;
// Grade -1 (Pre-K) → k4, 0 → k5, 1-8 → that grade, 9 → the track's first high-school course (Algebra 1,
// Integrated I or Integrated I Honors) … 12 → Precalculus, 13 (beyond) → Calculus I.
export const courseForGrade=(grade:number,track:Track=DEFAULT_TRACK):CourseId=>{const list=trackList(track);return list[Math.max(0,Math.min(grade+1,list.indexOf('calculus1')))]!;};
// The course after this one. For a course on any track it is the next school year on the given track, so a
// change of track takes effect here, at the next promotion, never mid-course. Without a track, the course's
// own. Null at the end of a track and for courses that lead nowhere on their own (SAT Math Mastery, the
// electives, the legacy course); finishedChoices (tracks.ts) says what the parent can pick instead.
export const nextCourse=(course:CourseId,track:Track=trackOfCourse(course)):CourseId|null=>{
  const side=SIDE_NEXT[course];if(side)return side;
  const i=trackPosition(BRIDGE_TO[course]??course),list=trackList(track);
  return i>=0&&i<list.length-1?list[i+1]!:null;
};
// One step along the track for the starting-point check: null off the tracks or past either end.
export const stepOnTrack=(course:CourseId,direction:'up'|'down',track:Track=trackOfCourse(course)):CourseId|null=>{
  const i=trackPosition(course),j=direction==='up'?i+1:i-1,list=trackList(track);
  return i>=0&&j>=0&&j<list.length?list[j]!:null;
};
